import { forwardRef, useState } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { Tabs as BaseTabs } from '@base-ui/react/tabs';
import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import { useRender } from '@base-ui/react/use-render';
import { Badge } from 'components/Badge';
import { cn } from 'components/utils';

import { TabsContext, useTabsContext } from './context';
import { isActiveRoute } from './isActiveRoute';
import {
  tabLabelRecipe,
  tabRecipe,
  tabsFadeRecipe,
  tabsIndicatorRecipe,
  tabsListRecipe,
  tabsRailRecipe,
  tabsRecipe,
  tabsScrollerRecipe,
  tabsTrackRecipe,
} from './Tabs.recipe';
import { useTabsStrip } from './useTabsStrip';

import type {
  FilterTabsProps,
  NavigationTabsProps,
  PanelsTabsProps,
  TabLinkProps,
  TabProps,
  TabsLayout,
  TabsPanelProps,
  TabsProps,
} from './types';

type FrameProps = Omit<ComponentPropsWithoutRef<'div'>, 'children'> & {
  layout: TabsLayout;
  children: ReactNode;
};

/**
 * Scrollport, indicator, fades and rail: everything about the strip that is the same in all three
 * modes. The mode's own element (a `tablist`, a group or a `<nav>`) is passed in as `children`.
 */
const TabsFrame = forwardRef<HTMLDivElement, FrameProps>(({ layout, className, children, ...rest }, ref) => {
  const { edges, indicatorRef, onKeyDownCapture, onPointerDownCapture, scrollerRef, trackRef } = useTabsStrip();

  return (
    <div
      {...rest}
      ref={ref}
      className={cn(tabsRecipe({ layout }), className)}
      data-layout={layout}
      data-overflow-end={edges.end || undefined}
      data-overflow-start={edges.start || undefined}
      data-part="root"
      onKeyDownCapture={onKeyDownCapture}
      onPointerDownCapture={onPointerDownCapture}
    >
      <div ref={scrollerRef} className={tabsScrollerRecipe()}>
        <div ref={trackRef} className={tabsTrackRecipe({ layout })}>
          {children}
          <span ref={indicatorRef} aria-hidden className={tabsIndicatorRecipe()} data-part="indicator" />
        </div>
      </div>
      <span aria-hidden className={tabsFadeRecipe({ edge: 'start' })} data-edge="start" />
      <span aria-hidden className={tabsFadeRecipe({ edge: 'end' })} data-edge="end" />
      <span aria-hidden className={tabsRailRecipe()} />
    </div>
  );
});

const PanelsTabs = forwardRef<HTMLDivElement, PanelsTabsProps>(
  (
    {
      mode: _mode,
      layout = 'hug',
      label,
      'aria-labelledby': labelledBy,
      value,
      defaultValue,
      onValueChange,
      activateOnFocus,
      panels,
      children,
      className,
      ...rest
    },
    ref
  ) => (
    <TabsContext.Provider value={{ mode: 'panels', layout }}>
      <BaseTabs.Root
        {...rest}
        ref={ref}
        className={className}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        value={value}
      >
        <TabsFrame layout={layout}>
          <BaseTabs.List
            activateOnFocus={activateOnFocus}
            aria-label={label}
            aria-labelledby={labelledBy}
            className={tabsListRecipe({ layout })}
            data-part="list"
          >
            {children}
          </BaseTabs.List>
        </TabsFrame>
        {panels}
      </BaseTabs.Root>
    </TabsContext.Provider>
  )
);

/**
 * A toggle group that always has one value pressed.
 *
 * Base UI's single-select group lets a person press the pressed option and end up with none. A
 * filter with no value is a state the table behind it has no answer for, so an empty change is
 * dropped. That needs the pressed value held here, not in Base UI: an uncontrolled group would
 * accept the empty change and show nothing pressed.
 */
const FilterTabs = forwardRef<HTMLDivElement, FilterTabsProps>(
  (
    {
      mode: _mode,
      layout = 'hug',
      label,
      'aria-labelledby': labelledBy,
      value,
      defaultValue,
      onValueChange,
      children,
      ...rest
    },
    ref
  ) => {
    const [held, setHeld] = useState(defaultValue);
    const pressed = value ?? held;

    return (
      <TabsContext.Provider value={{ mode: 'filter', layout }}>
        <TabsFrame {...rest} ref={ref} layout={layout}>
          <ToggleGroup
            aria-label={label}
            aria-labelledby={labelledBy}
            className={tabsListRecipe({ layout })}
            data-part="list"
            onValueChange={(next) => {
              const [picked] = next;

              if (picked === undefined) {
                return;
              }

              setHeld(picked);
              onValueChange?.(picked);
            }}
            value={pressed === undefined ? [] : [pressed]}
          >
            {children}
          </ToggleGroup>
        </TabsFrame>
      </TabsContext.Provider>
    );
  }
);

const NavigationTabs = forwardRef<HTMLDivElement, NavigationTabsProps>(
  ({ mode: _mode, layout = 'hug', label, 'aria-labelledby': labelledBy, pathname, children, ...rest }, ref) => (
    <TabsContext.Provider value={{ mode: 'navigation', layout, pathname }}>
      <TabsFrame {...rest} ref={ref} layout={layout}>
        <nav aria-label={label} aria-labelledby={labelledBy} className={tabsListRecipe({ layout })} data-part="list">
          {children}
        </nav>
      </TabsFrame>
    </TabsContext.Provider>
  )
);

/**
 * Design System 2.0 tabs: one strip for every tab row and every choose-one row in the product.
 *
 * Ported from Figma sets 2072:146115 (Tab) and 2072:148445 (Tabs). One look, three `mode`s that
 * differ only in what a screen reader is told and what a choice does:
 *
 *   `panels` (default)  `<Tabs label value onValueChange panels={…}>` with `Tab`s as children.
 *   `navigation`        `<Tabs mode="navigation" pathname label>` with `TabLink`s. The page keeps its
 *                       own routes and redirects; this only draws the links.
 *   `filter`            `<Tabs mode="filter" label value onValueChange>` with `Tab`s. One value is
 *                       always pressed.
 *
 * Use `Tabs` for views of the same thing: this week / this month, or the sections of one record.
 * A row of unrelated destinations is navigation, a bottom bar is its own pattern, and one option is
 * a heading, not a strip.
 *
 * The strip scrolls sideways when it runs out of room and never wraps, with an edge fade where more
 * is hidden. The chosen tab is scrolled into view. `layout="equal"` shares the width instead.
 * Horizontal only.
 */
export const Tabs = forwardRef<HTMLDivElement, TabsProps>((props, ref) => {
  if (props.mode === 'filter') {
    return <FilterTabs {...props} ref={ref} />;
  }

  if (props.mode === 'navigation') {
    return <NavigationTabs {...props} ref={ref} />;
  }

  return <PanelsTabs {...props} ref={ref} />;
});

const TabContent = ({ badge, children }: Pick<TabProps, 'badge' | 'children'>) => (
  <>
    <span className={tabLabelRecipe()}>{children}</span>
    {badge ? <Badge color="neutral" {...badge} /> : null}
  </>
);

/** One tab of a `panels` or `filter` strip. */
export const Tab = forwardRef<HTMLButtonElement, TabProps>(
  ({ value, badge, children, className, disabled, ...rest }, ref) => {
    const { mode, layout } = useTabsContext('Tab');
    const classes = cn(tabRecipe({ layout }), className);

    if (mode === 'panels') {
      return (
        <BaseTabs.Tab {...rest} ref={ref} className={classes} data-part="tab" disabled={disabled} value={value}>
          <TabContent badge={badge}>{children}</TabContent>
        </BaseTabs.Tab>
      );
    }

    if (mode === 'filter') {
      return (
        <Toggle {...rest} ref={ref} className={classes} data-part="tab" disabled={disabled} value={value}>
          <TabContent badge={badge}>{children}</TabContent>
        </Toggle>
      );
    }

    throw new Error('Tab belongs in a panels or filter strip. Inside mode="navigation" use TabLink.');
  }
);

/**
 * A tab that navigates. The anchor is the element, so open in a new tab, copy link and the link role
 * all work; `aria-current="page"` marks the current one. Not built on Base UI's `Tab`, which would
 * announce a link as a tab.
 */
export const TabLink = forwardRef<HTMLAnchorElement, TabLinkProps>(
  ({ href, badge, children, className, render, ...rest }, ref) => {
    const { mode, layout, pathname } = useTabsContext('TabLink');

    if (mode !== 'navigation') {
      throw new Error('TabLink belongs in mode="navigation". Elsewhere use Tab.');
    }

    return useRender({
      ref,
      render: render ?? <a />,
      props: {
        ...rest,
        href,
        className: cn(tabRecipe({ layout }), className),
        'aria-current': isActiveRoute(pathname ?? '', href) ? 'page' : undefined,
        'data-part': 'tab',
        children: <TabContent badge={badge}>{children}</TabContent>,
      },
    });
  }
);

/**
 * A panel of a `panels` strip, linked to its tab by ids Base UI derives from the mounted panels, so
 * two strips on one page never share one. Unmounted while hidden unless `keepMounted`.
 */
export const TabsPanel = forwardRef<HTMLDivElement, TabsPanelProps>(
  ({ value, keepMounted, className, children, ...rest }, ref) => {
    const { mode } = useTabsContext('TabsPanel');

    if (mode !== 'panels') {
      throw new Error('TabsPanel belongs in a panels strip.');
    }

    return (
      <BaseTabs.Panel {...rest} ref={ref} className={className} keepMounted={keepMounted} value={value}>
        {children}
      </BaseTabs.Panel>
    );
  }
);
